import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Output,
  Renderer2,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ImageService } from 'src/app/common/services/image/image.service';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';

import jwt_decode from 'jwt-decode';
import { SocialLoginComponent } from '../social.login.component';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ActivatedRoute } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { CheckLoginStatusService } from '../checkLoginStatus.service';

@Component({
  selector: 'ig-google-login',
  templateUrl: './google.login.component.html',
  styleUrls: ['./google.login.component.scss'],
})
export class GoogleLoginComponent extends BaseComponent {
  public auth2: any;
  public profilePicture: string;
  public static isUserLoggedIn: boolean;
  public static googleUserId: string;
  public static profilePicture: string;
  public static email: string;
  public static userName: string;

  SCRIPT_PATH = `https://accounts.google.com/gsi/client`;

  // notify SocialLoginComponent when google library is loaded, then show Facebook login button too
  public static googleReady$ = new BehaviorSubject(false);

  constructor(
    public activatedRoute: ActivatedRoute,
    public imageService: ImageService,
    public scriptService: ScriptService,
    private _renderer: Renderer2,
    private checkLoginStatusService: CheckLoginStatusService,
    @Inject(DOCUMENT) private _document: Document
  ) {
    super({ activatedRoute });

    // Only load Google library if not in webview
    if (!this.isInWebView()) {
      try {
        this.loadGoogleLibrary();
      } catch (e) {
        console.warn(e);
      }
    } else {
      // In webview, mark as ready to show Facebook login
      GoogleLoginComponent.googleReady$.next(true);
    }
  }

  public loadGoogleLibrary() {
    // Skip if in webview - app handles Google OAuth
    if (this.isInWebView()) {
      GoogleLoginComponent.googleReady$.next(true);
      return;
    }

    if (!this.scriptService.isScriptExist(this.SCRIPT_PATH)) {
      const scriptElement = this.scriptService.loadJsScript(
        this._renderer,
        this.SCRIPT_PATH
      );
      scriptElement.onload = () => {
        console.log('Google login script loaded');
        // notify SocialLoginComponent when google library is loaded, then show Facebook login button
        GoogleLoginComponent.googleReady$.next(true);
        this.initializeGoogle();
      };
      scriptElement.onerror = () => {
        console.log('Could not load the script: ' + this.SCRIPT_PATH);
      };
    }
  }

  @HostListener('window:onGoogleLibraryLoad', ['$event'])
  public initializeGoogle() {
    this.requestAppLogin();
    console.log('initializing google');

    // check if user is already logged in from local storage, as Google cannot remember login status
    const googleUser = localStorage.getItem('social.login.googleUser');
    if (googleUser) {
      const googleUserObj = JSON.parse(googleUser);
      console.log('googleUserLoaded in LocalStorage', googleUserObj);
      GoogleLoginComponent.isUserLoggedIn = true;
      GoogleLoginComponent.googleUserId = googleUserObj.userId;
      GoogleLoginComponent.profilePicture = googleUserObj.profilePicture;
      GoogleLoginComponent.email = googleUserObj.email;
      GoogleLoginComponent.userName = googleUserObj.userName;
      SocialLoginComponent.loginStatus$.next(true);
    }

    const google = (window as any).google;

    if (google?.accounts?.id?.initialize) {
      google['accounts'].id.initialize({
        client_id: '665289826545-463bhl103plveo5l05b94vjnenmr3f7e',
        callback: (data) => {
          console.log('google initialized', data);

          if (data['credential']) {
            const credential = jwt_decode(data['credential']);
            console.log('credential', credential);
            this.profilePicture = credential['picture'];
            GoogleLoginComponent.isUserLoggedIn = true;
            GoogleLoginComponent.googleUserId = credential['email'] + '|google';
            GoogleLoginComponent.email = credential['email'];
            GoogleLoginComponent.userName = credential['name'];
            GoogleLoginComponent.profilePicture = this.profilePicture;
            console.log(`googleId: ${GoogleLoginComponent.googleUserId}`);

            // Google cannot remember login status, so save it in localStorage
            localStorage.setItem(
              'social.login.googleUser',
              JSON.stringify({
                userId: GoogleLoginComponent.googleUserId,
                profilePicture: GoogleLoginComponent.profilePicture,
                email: GoogleLoginComponent.email,
                userName: GoogleLoginComponent.userName,
              })
            );
            this.checkLoginStatusService.checkGoogleLoginStatus();
            SocialLoginComponent.loginStatus$.next(true);
          }
        },
      });
    }

    this.renderGoogleButtons();
  }

  private renderGoogleButtons(): void {
    // 1. Read the global symbol again inside THIS function
    const google = (window as any).google;

    // 2. Bail out if the SDK still isn’t loaded
    if (!google?.accounts?.id?.renderButton) {
      console.warn('[GoogleLogin] GIS not ready – skip renderButton() call');
      return;
    }

    const loginBtnItems = (this.shadowRoot || this._document).querySelectorAll(
      '.google-signin-btn'
    );

    const loginBtnItemsInPopup = this._document.querySelectorAll(
      '.cdk-overlay-container .google-signin-btn'
    );

    Array.from(loginBtnItems)
      .concat(Array.from(loginBtnItemsInPopup))
      .forEach((el) => {
        google.accounts.id.renderButton(el, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: window.innerWidth >= 400 ? 295 : 260,
        });
      });
  }

  // Static method to update Google login status from app-level OAuth
  public static updateFromAppLogin(loginData: any) {
    if (loginData.isLoggedIn && loginData.provider === 'google') {
      GoogleLoginComponent.isUserLoggedIn = true;
      GoogleLoginComponent.googleUserId = loginData.userId;
      GoogleLoginComponent.email = loginData.email;
      GoogleLoginComponent.userName = loginData.userName;
      GoogleLoginComponent.profilePicture = loginData.profilePicture;

      // Store in localStorage for compatibility with existing code
      localStorage.setItem(
        'social.login.googleUser',
        JSON.stringify({
          userId: loginData.userId,
          profilePicture: loginData.profilePicture,
          email: loginData.email,
          userName: loginData.userName,
        })
      );

      SocialLoginComponent.loginStatus$.next(true);
    } else {
      GoogleLoginComponent.clearLoginData();
    }
  }

  // Static method to clear Google login data
  public static clearLoginData() {
    GoogleLoginComponent.isUserLoggedIn = false;
    GoogleLoginComponent.googleUserId = null;
    GoogleLoginComponent.profilePicture = null;
    GoogleLoginComponent.email = null;
    GoogleLoginComponent.userName = null;
    localStorage.removeItem('social.login.googleUser');
    SocialLoginComponent.loginStatus$.next(false);
  }

  // Check if we're in a webview
  private isInWebView(): boolean {
    return (
      window.parent !== window ||
      navigator.userAgent.includes('wv') || // Android WebView
      (navigator.userAgent.includes('Version/') &&
        navigator.userAgent.includes('Mobile/'))
    ); // iOS WebView
  }

  // Request app-level Google login
  private requestAppLogin() {
    const message = {
      type: 'APP_LOGIN_REQUEST',
      provider: 'google',
    };

    // if (window.parent !== window) {
    //   window.parent.postMessage(message, '*');
    // }
    
     const host = (window.parent && window.parent !== window)
    ? window.parent      // ↖ running in an iframe – use parent
    : window; 
    console.log('request to app login message', message);
    window.postMessage(message, '*')
  }

  // Request app-level Google logout
  private requestAppLogout() {
    const message = {
      type: 'APP_LOGOUT_REQUEST',
      provider: 'google',
    };

    // if (window.parent !== window) {
    //   window.parent.postMessage(message, '*');
    // }
    
     const host = (window.parent && window.parent !== window)
    ? window.parent      // ↖ running in an iframe – use parent
    : window; 
    console.log('request to app logout message', message);
    window.postMessage(message, '*')
  }

  public login() {
    if (this.isInWebView()) {
      this.requestAppLogin();
    } else {
      // Trigger Google login for non-webview environments
      google['accounts'].id.prompt();
    }
  }

  public go() {
    // Development/testing method - keep as is
    GoogleLoginComponent.isUserLoggedIn = true;
    GoogleLoginComponent.googleUserId = '117302739800456246841|google';
    GoogleLoginComponent.profilePicture =
      'https://lh3.googleusercontent.com/a/AGNmyxbLIc5wFVwhFt0S3nko5iUrXK4tCh1gLndHliWk=s96-c';
    SocialLoginComponent.loginStatus$.next(true);
  }

  public logout(): void {
    /* ---- 1. If the component is embedded in your mobile-app web-view ---- */
    if (this.isInWebView()) {
      // tell the container app to sign the user out
      this.requestAppLogout();
      GoogleLoginComponent.clearLoginData();
      return; // <-- do NOT fall through
    }

    /* ---- 2. Regular browser tab ---- */
    // Only touch the GIS SDK when it is really present
    const g = (window as any).google;
    if (g?.accounts?.id?.disableAutoSelect) {
      g.accounts.id.disableAutoSelect();
    }

    GoogleLoginComponent.clearLoginData();
  }

  public get isUserLoggedWithGoogle() {
    return GoogleLoginComponent.isUserLoggedIn;
  }

  // Method to check login status on component initialization
  public checkInitialLoginStatus() {
    // Check if there's existing login data in localStorage
    const storedGoogleUser = localStorage.getItem('social.login.googleUser');
    if (storedGoogleUser) {
      try {
        const userData = JSON.parse(storedGoogleUser);
        GoogleLoginComponent.isUserLoggedIn = true;
        GoogleLoginComponent.googleUserId = userData.userId;
        GoogleLoginComponent.profilePicture = userData.profilePicture;
        GoogleLoginComponent.email = userData.email;
        GoogleLoginComponent.userName = userData.userName;
        SocialLoginComponent.loginStatus$.next(true);
      } catch (e) {
        console.warn('Error parsing stored Google user data:', e);
        localStorage.removeItem('social.login.googleUser');
      }
    }
  }
}
